import { useCallback, useEffect, useState } from 'react'

import { Drawer, Empty, Input, List, Modal } from 'antd'
import { faGraduationCap, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'

import { isNonEmptyArray, isObject } from '../../../../utils'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../../reducers/UserReducer'
import { getUserCoordinatedSections, linkUserToSections, unlinkSectionFromUser } from '../../../../utils/api/user'
import IconButton from '../../../../Components/antd/Buttons/IconButton'
import Loading from '../../../../Components/shared/Loading'
import SchoolSectionsSelector from '../../../../Components/shared/Selectors/SchoolSectionsSelector'
import FormItem from '../../../../Components/shared/Forms/FormItem'
import { onError, onSuccess } from '../../../../utils/apiHelper'

const SupervisorSections = ({ supervisor, onClose }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [schoolSections, setSchoolSections] = useState([])
  const [displayModal, setDisplayModal] = useState(false)
  const [sectionsToLink, setSectionsToLink] = useState([])

  const fetchSchoolSections = useCallback(supervisor => {
    setLoading(true)

    getUserCoordinatedSections(user, supervisor).then(({ data }) => {
      setSchoolSections(data)
    }).finally(() => setLoading(false))
  }, [user.id])

  useEffect(() => {
    if (isObject(supervisor)) {
      fetchSchoolSections(supervisor)
    }
  }, [fetchSchoolSections, supervisor])

  const resetStates = visible => {
    if (!visible) {
      setSectionsToLink([])
    }
  }

  const displayedSchoolSections = schoolSections.filter(ss => {
    let name = ''

    name = `${ss.name} (${ss.schoolYearName})`

    return name.toLowerCase().includes(search.toLowerCase())
  })

  const handleUnlinkSection = section => {
    unlinkSectionFromUser(user, supervisor, section).then(() => {
      setSchoolSections(prev => prev.filter(ss => ss.id !== section.id))

      onSuccess(t('user.unlink_section.success'))
    }).catch(error => {
      onError(t('user.unlink_section.error'))
      console.error(error)
    })
  }

  const handleLinkSections = () => {
    if (isNonEmptyArray(sectionsToLink)) {
      linkUserToSections(user, supervisor, sectionsToLink).then(() => {
        fetchSchoolSections(supervisor)
        onSuccess(t('user.link_sections.success'))
      }).catch(error => {
        onError(t('user.link_sections.error'))
        console.error(error)
      }).finally(() => {
        setSectionsToLink([])
        setDisplayModal(false)
      })
    }
  }

  return (
    <>
      <Drawer
        title={t('supervisor_sections.drawer.title', { name: isObject(supervisor) ? `${supervisor.firstname} ${supervisor.lastname}` : '' })}
        visible={isObject(supervisor)}
        width={500}
        onClose={onClose}
        afterVisibleChange={resetStates}
      >
        <div className='supervised-sectors-container'>
          <div className='actions-list'>
            <Input.Search onChange={e => setSearch(e.target.value)} />
            <IconButton icon={faPlus} text={t('supervisor_sections.drawer.add_button')} type='primary' onClick={() => setDisplayModal(true)} />
          </div>
          {!loading && schoolSections.length > 0 && (
            <div className='supervised-sectors-list'>
              <List>
                {displayedSchoolSections.map((ss, index) => {
                  return (
                    <List.Item key={'list-item_' + ss.id}>
                      <div className='list-item-container'>
                        <span className='list-item-value'>
                          <b> {ss.name} </b> - ({ss.schoolYearName})
                        </span>
                        <IconButton icon={faTrash} type='danger' onClick={() => handleUnlinkSection(ss)} />
                      </div>
                    </List.Item>
                  )
                })}
              </List>
            </div>
          )}
          {!loading && schoolSections.length === 0 && (
            <>
              <div className='v-spacing' />
              <Empty description={t('supervisor_sections.drawer.no_sections_linked')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </>
          )}
          {loading && (<Loading size='4x' style={{ marginTop: '30px' }} />)}
        </div>
      </Drawer>
      <Modal
        title={t('supervisor_sections.modal.title')}
        visible={displayModal}
        onCancel={() => setDisplayModal(false)}
        onOk={handleLinkSections}
        okText={t('common.link')}
        cancelText={t('common.cancel')}
        okButtonProps={{ disabled: sectionsToLink.length === 0 }}
      >
        <FormItem icon={faGraduationCap} label='supervisor_sections.modal.form_title'>
          <SchoolSectionsSelector
            style={{ width: '100%' }}
            multiple='multiple'
            values={sectionsToLink}
            onChange={setSectionsToLink}
          />
        </FormItem>
      </Modal>
    </>
  )
}

export default SupervisorSections
